import { useCallback } from 'react'

import { format } from 'date-fns'
import { excelSerialToDate, convertExcelTimeToHHMM, normalizeTime } from 'utils/convertFromLocalDate'

import type { ValueFormatterParams, ValueParserParams, ExcelDataType
  , ValueGetterParams, ValueSetterParams
} from 'ag-grid-community'
import type { IRichCellEditorParams } from 'ag-grid-enterprise'
import type { Codes } from 'api-client'

/**
 * ag-Grid用の値フォーマッター・パーサーフックス
 * データ型変換、バリデーション、フォーマット機能を提供
 */
export const useGridFormatters = () => {

  const editInfoValueGetter = useCallback((params: ValueGetterParams) => {
    const rowData = params.data as typeof params.data
    if (rowData.errors && Object.keys(rowData.errors).length > 0) {
      return '入力エラー'
    }
    if (rowData.isEdited && rowData.editedFields.size > 0) {
      return '変更'
    }
    return null
  }, [])

  const isNonInputValue = (value: any): boolean =>
    value === null || value === undefined || value === '' || value === '-';


  const parseNumeric = useCallback((value: any): number | null => {
    if (isNonInputValue(value)) return null;
    const numericValue = Number(value);
    if (Number.isNaN(numericValue) || numericValue < 0) return null;

    return numericValue;
  }, [])

  const parsePositiveInteger = useCallback((value: any): number | null => {
    const numericValue = parseNumeric(value);
    if (numericValue === null) return null;
    if (numericValue <= 0) return null;
    return Math.floor(numericValue);
  }, [])

  const parsePositiveIntegerZero = useCallback((value: any): number | null => {
    const numericValue = parseNumeric(value);
    if (numericValue === null) return null;
    if (numericValue < 0) return null;
    return Math.floor(numericValue);
  }, [])

  const parseNumericWithRange = useCallback((value: any): number | null => {
    if (isNonInputValue(value)) return null;
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return null;
    if (numericValue < 1 || numericValue > 100) return null;
    return Math.floor(numericValue);
  }, []);

  const parseNumericWithRangeZero = useCallback((value: any): number | null => {
    if (isNonInputValue(value)) return null;
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return null;
    if (numericValue < 0 || numericValue > 100) return null;
    return Math.floor(numericValue);
  }, []);

  const MIN_DATE = new Date(1900, 2, 1);
  const MAX_DATE = new Date(2262, 3, 11);

  const parseDate = useCallback((value: any): Date | null | undefined => {
    if (isNonInputValue(value)) return null;
    const parsedDate = typeof value === 'number' ? excelSerialToDate(value) : new Date(value);
    if (!parsedDate) return null;
    if (parsedDate < MIN_DATE || parsedDate >= MAX_DATE) return null;
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }, [])

  const parseDateValue = (params: ValueParserParams): Date | null | undefined =>
    parseDate(params.newValue)

  const dateFormatter = useCallback((params: ValueFormatterParams<Date | null>) => {
    const date = parseDate(params.value);
    return date ? format(date, 'yyyy/MM/dd') : '';
  }, [])

  const dateTimeFormatter = useCallback((params: ValueFormatterParams<Date | null>) => {
    const date = parseDate(params.value)
    return date ? format(date, 'yyyy/MM/dd HH:mm:ss') : ''
  }, [])

  const parseTime = useCallback((value: any): string | null | undefined => {
    if (isNonInputValue(value)) return null;
    let parsedTime = null;
    if (typeof value === 'number') {
      parsedTime = convertExcelTimeToHHMM(value)?.toString();
    } else if (typeof value === 'string') {
      parsedTime = normalizeTime(value)
    } else {
      parsedTime = null;
    }
    return parsedTime;
  }, [])

  const timeParser = (params: ValueParserParams): string | null | undefined =>
    parseTime(params.newValue)

  const parseStringValue = useCallback((params: ValueParserParams): string | null => {
    const { newValue } = params;
    if (isNonInputValue(newValue)) return null;
    return newValue.toString();
  }, [])

  const longTextValueParser = useCallback((params: ValueParserParams): string | null => {
    let { newValue } = params
    if (isNonInputValue(newValue)) return null
    if (typeof newValue === 'string') {
      if (/^".*"$/.test(newValue) && /[\r\n]/.test(newValue)) {
        newValue = newValue.substring(1, newValue.length - 1)
      }
    }
    return newValue
  }, [])

  const longTextValueFormatter = (params: ValueFormatterParams): string => {
    if (isNonInputValue(params.value)) return ''
    const text = params.value?.toString() ?? ''
    if (/[\r\n]/.test(text) && !(text.startsWith('"') && text.endsWith('"'))) {
      return `"${text}"`
    }
    return text
  }

  const longTextValueSetter = useCallback(
    (params: ValueSetterParams): boolean => {
      const parsed = longTextValueParser(params)
      const { oldValue, data, colDef } = params
      const oldVal = oldValue as string
      if (parsed === oldVal) {
        return false
      }
      data[colDef.field!] = parsed
      return true
    },
    [longTextValueParser]
  )

  const longTextEditorParams = (params: any) => {
    let raw = params.value != null ? params.value.toString() : ''
    if (raw.startsWith('"') && raw.endsWith('"') && /[\r\n]/.test(raw)) {
      raw = raw.slice(1, -1)
    }
    return {
      maxLength: 1000,
      value: raw,
    }
  }

  const numberValueFormatter = (params: ValueFormatterParams): string =>
    isNonInputValue(params.value) || Number.isNaN(params.value) || params.value < 0 ? '-' : params.value.toString();

  const numberValueParser = useCallback((params: ValueParserParams): number | null =>
    parsePositiveInteger(params.newValue)
    , [])

  const numberValueParserZero = useCallback((params: ValueParserParams): number | null =>
    parsePositiveIntegerZero(params.newValue)
    , [])

  // const numberValueParser = useCallback((params: ValueParserParams): number | null => {
  //   const { newValue, node, colDef } = params;
  //   const field = colDef.field!;
  //   const parsedValue = parsePositiveInteger(newValue);
  //   if (node) node.setDataValue(field, parsedValue);
  //   return parsedValue;
  // }, [])

  const numberRangeValueParser = useCallback((params: ValueParserParams): number | null => {
    const { newValue } = params;
    const parsedValue = parseNumericWithRange(newValue);
    return parsedValue;
  }, [])

  const numberRangeValueParserZero = useCallback((params: ValueParserParams): number | null => {
    const { newValue } = params;
    const parsedValue = parseNumericWithRangeZero(newValue);
    return parsedValue;
  }, [])

  const formatDecimalValue = useCallback((params: ValueFormatterParams, decimalPlaces = 2): string => {
    if (isNonInputValue(params.value)) return '-';

    const numericValue = parseNumeric(params.value);
    if (numericValue === null) return params.value?.toString() || '';
    if (decimalPlaces === 0) {
      return Math.floor(numericValue).toString();
    }
    const multiplier = 10 ** decimalPlaces;
    const rounded = Math.round(numericValue * multiplier) / multiplier;
    return rounded.toFixed(decimalPlaces);
  }, [])

  const parseDecimalValue = useCallback((params: ValueParserParams, decimalPlaces = 2): number | null => {
    const numericValue = parseNumeric(params.newValue);
    if (numericValue === null || numericValue < 0) return null;

    const multiplier = 10 ** decimalPlaces;
    return Math.round(numericValue * multiplier) / multiplier;
  }, [])

  const TRUE_VALUES = ['T', 't', 'TRUE', 'true', 1, '1', '１', true];

  const formatBooleanValue = useCallback((params: ValueFormatterParams): string => {
    const value = params.value ?? false;
    return value ? 'True' : 'False';
  }, [])

  const parseBoolean = useCallback((value: any): boolean => {
    let parsedValue = false;
    if (typeof value === 'boolean') {
      parsedValue = Boolean(value);
    } else if (isNonInputValue(value)) {
      parsedValue = false;
    } else {
      parsedValue = TRUE_VALUES.includes(String(value).toLowerCase());
    }
    return parsedValue;
  }, [])

  const parseBooleanValue = useCallback((params: ValueParserParams): boolean => {
    const { newValue, node, colDef } = params;
    const field = colDef.field!;
    const parsedValue = parseBoolean(newValue);

    if (node) {
      node.setDataValue(field, parsedValue);
    }
    return parsedValue;
  }, [])

  const booleanFormatter = useCallback((params: ValueFormatterParams<boolean | null>) => {
    if (params.value === true) {
      return 'true';
    }
    if (params.value === false) {
      return 'false';
    }
    return '';
  }, [])

  const selectValueFormatter = useCallback((value: string, datalist: any[], keyField: string, nameField: string) => {
    if (isNonInputValue(value)) return '-';

    const vp = datalist.find((s) => s[keyField] === value);
    return vp ? `[${vp[keyField]}]${vp[nameField]}` : value;
  }, [])

  const selectValueParser = useCallback((params: ValueParserParams, datalist: any[], keyField: string): any | null => {
    const { newValue } = params;
    if (isNonInputValue(newValue)) return null;

    let vp = datalist.find((s) => s[keyField] === newValue);

    if (!vp && String(newValue).startsWith('[') && String(newValue).indexOf(']') > 1) {
      const codematch = String(newValue).match(/\[(.+)\]/);
      const code = codematch ? codematch[1] : '';
      vp = datalist.find((s) => s[keyField] === code);
    }
    return vp ? vp[keyField] : null;
  }, [])

  const typecodeFormatter = useCallback((value: string, datalist: any[]) => {
    if (isNonInputValue(value) || !datalist) return '-';

    const vp = datalist.find((s) => s.typeCode === value);
    return vp ? `${vp.typeName}` : value;
  }, [])

  const typeCodeParser = useCallback((value: string, datalist: any[]) => {
    if (isNonInputValue(value)) return null

    let vp = datalist.find((s) => s.typeCode === String(value))
    // typecodeの場合は、区分値も受け入れ可能
    if (!vp) {
      vp = datalist.find((s) => s.typeName === String(value))
    }

    if (!vp && String(value).startsWith('[') && String(value).indexOf(']') > 1) {
      const codematch = String(value).match(/\[(.+)\]/)
      const code = codematch ? codematch[1] : ''
      vp = datalist.find((s) => s.typeCode === code)
    }
    return vp ? vp.typeCode : null

  }, [])

  const selectTypecodeValueParser = useCallback((params: ValueParserParams, datalist: any[]): any | null => {
    const { newValue } = params;
    return typeCodeParser(newValue, datalist);
  }, [])

  /**
   * Multi-select typecode formatter
   * Formats an array of type codes to a comma-separated string of type names
   */
  const multiSelectTypecodeFormatter = useCallback((params: ValueFormatterParams, typeList: Codes[]): string => {
    const { value } = params
    if (!value || !Array.isArray(value) || value.length === 0) return ''
    
    const formatted = value
      .map((code) => {
        const codeItem = typeList.find((item) => item.typeCode === code)
        return codeItem ? codeItem.typeName : code
      })
      .join(', ')
    return formatted
  }, [])

  /**
   * Multi-select typecode parser
   * Parses array or single value to array of type codes
   * Handles special values: __ALL__ (select all) and __CLEAR_ALL__ (clear all)
   * Returns sorted array based on typeList order
   */
  const multiSelectTypecodeParser = useCallback((params: ValueParserParams, typeList: Codes[]): string[] => {
    const { newValue } = params
    const allTypeCodes = typeList.map(item => item.typeCode)
    const ALL_VALUE = '__ALL__'
    const CLEAR_ALL_VALUE = '__CLEAR_ALL__'
    
    // agRichSelectCellEditor with multiSelect returns array
    let resultStr: string[] = []
    if (Array.isArray(newValue)) {
      // if "__CLEAR_ALL__" is selected, delete all
      if (newValue.includes(CLEAR_ALL_VALUE)) {
        resultStr = []
      } else {
        resultStr = newValue.filter(v => v != null && v !== ALL_VALUE && v !== CLEAR_ALL_VALUE)
        // if "__ALL__" is selected, select all
        if (newValue.includes(ALL_VALUE)) {
          resultStr = [...allTypeCodes]
        }
      }
    } else if (newValue) {
      if (newValue === CLEAR_ALL_VALUE) {
        resultStr = []
      } else if (newValue === ALL_VALUE) {
        resultStr = [...allTypeCodes]
      } else {
        const str = String(newValue).trim()
        if (str.includes(',')) {
          // Paste: "1, 2, 3, 4" -> split and parse each to typeCode, keep only valid
          resultStr = str
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
            .reduce<string[]>((acc, part) => {
              const code = typeCodeParser(part, typeList)
              if (code) acc.push(code)
              return acc
            }, [])
        } else {
          // Single value (e.g. paste "11:11"): only accept if valid typeCode/typeName
          const code = typeCodeParser(str, typeList)
          resultStr = code ? [code] : []
        }
      }
    }
    
    // sort by index in typeList (small to large)
    return resultStr.sort((a, b) => {
      const indexA = typeList.findIndex(item => item.typeCode === a)
      const indexB = typeList.findIndex(item => item.typeCode === b)
      // if not found in list, set at the end
      if (indexA === -1 && indexB === -1) return 0
      if (indexA === -1) return 1
      if (indexB === -1) return -1
      return indexA - indexB
    })
  }, [typeCodeParser])

  /**
   * Multi-select typecode filter value getter
   * Gets the formatted value from row data for filtering
   */
  const multiSelectTypecodeFilterValueGetter = useCallback((params: ValueGetterParams, field: string, typeList: Codes[]): string => {
    const value = params.data[field]
    if (!value || !Array.isArray(value)) return ''
    
    return value
      .map((code) => {
        const codeItem = typeList.find((item) => item.typeCode === code)
        return codeItem ? codeItem.typeName : code
      })
      .join(', ')
  }, [])

  /**
   * Multi-select typecode cell editor params
   * Creates cell editor parameters for agRichSelectCellEditor with multi-select support
   * Includes special values: __ALL__ (すべて選択) and __CLEAR_ALL__ (すべて解除)
   */
  const multiSelectTypecodeCellEditorParams = useCallback((
    editorDataReady: boolean,
    typeList: Codes[],
    typeCodeSelects: (string | null)[]
  ): IRichCellEditorParams => {
    if (!editorDataReady) {
      return { values: ['-'] } as IRichCellEditorParams
    }

    return {
      values: ['__ALL__', '__CLEAR_ALL__', ...typeCodeSelects],
      multiSelect: true,
      formatValue: (value: string) => {
        if (value === '__ALL__') {
          return 'すべて選択'
        }
        if (value === '__CLEAR_ALL__') {
          return 'すべて解除'
        }
        return selectValueFormatter(value, typeList, 'typeCode', 'typeName')
      },
    } as IRichCellEditorParams
  }, [selectValueFormatter])


  // Normalize empty values: null, undefined, "" should all be treated as null
  const normalizeEmpty = (val: any) => {
    if (isNonInputValue(val)) return null
    return val
  }

  const createCellClassRules = useCallback((
    targetField: string,
    expectedValue: any,
    cssClassName: string
  ) => ({
    [cssClassName]: (params: any) => params?.data?.[targetField] === expectedValue,
  }), [])

  const isSameArrayUnordered = (a: unknown[], b: unknown[]): boolean => {
    if (a.length !== b.length) return false
    const sortedA = [...a].sort()
    const sortedB = [...b].sort()
    return sortedA.every((value, index) => value === sortedB[index])
  }

  const excelStyles = [
    {
      id: 'numberType',
      numberFormat: {
        format: '#,##0',
      },
    },
    {
      id: 'decimalType',
      numberFormat: {
        format: '#,##0.000',
      },
    },
    {
      id: 'booleanType',
      dataType: 'Boolean' as ExcelDataType,
    },
    {
      id: 'stringType',
      dataType: 'String' as ExcelDataType,
    },
    {
      id: 'dateType',
      dataType: 'DateTime' as ExcelDataType,
      numberFormat: {
        format: 'yyy-mm-ddThh:mm:ss',
      },
    },
  ]

  return {
    booleanFormatter,
    dateFormatter,
    dateTimeFormatter,
    excelStyles,
    selectValueFormatter,
    typecodeFormatter,
    formatBooleanValue,
    formatDecimalValue,
    isNonInputValue,
    numberValueFormatter,
    numberValueParser,
    numberValueParserZero,
    parseBoolean,
    parseBooleanValue,
    parseDate,
    parseDateValue,
    parseDecimalValue,
    parseNumeric,
    parsePositiveInteger,
    parsePositiveIntegerZero,
    parseNumericWithRange,
    parseNumericWithRangeZero,
    parseStringValue,
    numberRangeValueParser,
    parseTime,
    timeParser,
    selectTypecodeValueParser,
    selectValueParser,
    typeCodeParser,
    multiSelectTypecodeFormatter,
    multiSelectTypecodeParser,
    multiSelectTypecodeFilterValueGetter,
    multiSelectTypecodeCellEditorParams,
    longTextValueParser,
    longTextValueFormatter,
    longTextEditorParams,
    longTextValueSetter,
    editInfoValueGetter,
    normalizeEmpty,
    createCellClassRules,
    numberRangeValueParserZero,
    isSameArrayUnordered
  }
}
